import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { ICashFlowForm } from 'services/cash-flow/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { getDefaultCurrency } from 'store/state';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { lazy, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import CashFlowAddFileForm from './tabs/CashFlowAddFileForm';
import CashFlowContracts from './tabs/CashFlowContracts';
import CashFlowRepayments from './tabs/CashFlowRepayments';

export enum CASH_FLOW_TABS {
  repayments,
  file,
  contracts,
}

const validationSchema = object({}).shape({
  name: string().required(() => i18next.t('formik-error.required')),
  tax: number().required(() => i18next.t('formik-error.required')),
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  amountNet: lazy((_, options) =>
    number().when('amountGross', {
      is: (gross: number | null) => gross === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
  amountGross: lazy((_, options) =>
    number().when('amountNet', {
      is: (net: number | null) => net === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
});

export const cashFlowInitialValues: ICashFlowForm = {
  amountGross: null,
  amountNet: null,
  tax: null,
  name: '',
  settlementId: '',
  settlementName: '',
  fileUrl: '',
  repayments: [],
  contracts: [],
  settlementPeriod: null,
  settlementPeriodId: '',
};

interface ICashFlowFormProps extends FormikConfig<ICashFlowForm>, IFormProps {}

const CashFlowForm = ({
  customBackItem,
  status,
  tittle,
  backPath,
  topbarContent,
  disableTabs,
  initialValues,
  ...props
}: ICashFlowFormProps) => {
  const { t } = useTranslation();

  const defaultCurrency = getDefaultCurrency()?.currencyCode ?? '';

  const isRepaymentsFromAllContracts = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  const settlementPeriods = useGetSettlementLightList();
  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      {...props}>
      {({ values, setFieldValue }: IFormikProps<ICashFlowForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'label'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  <div className='flex w-full'>
                    {status?.displayMode ? null : (
                      <div className='w-full max-w-60'>
                        <FormInput.Text
                          name='name'
                          label={t('input.label.name')}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                  <div>{topbarContent}</div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={2}>
                  <FormInput.Number
                    name='amountNet'
                    label={t('input.label.net')}
                    placeholder={`0 ${defaultCurrency}`}
                    suffix={` ${defaultCurrency}`}
                  />
                  <FormInput.Number
                    name='amountGross'
                    label={t('input.label.gross')}
                    placeholder={`0 ${defaultCurrency}`}
                    suffix={` ${defaultCurrency}`}
                  />
                  <FormInput.Number
                    name='tax'
                    label={t('input.label.tax')}
                    placeholder='0 %'
                    suffix=' %'
                  />
                  <FormInput.Select
                    name='settlementPeriodId'
                    label={t('input.label.settlementPeriodId')}
                    options={settlementPeriodOptions}
                    onChange={option => {
                      const [name, year] = option.label.split(' ');
                      setFieldValue('settlementPeriod', {
                        name: name,
                        year: parseInt(year),
                      });
                    }}
                    value={
                      values.settlementPeriod
                        ? {
                            value: values.settlementPeriodId,
                            label: `${values.settlementPeriod.name} ${values.settlementPeriod.year}`,
                          }
                        : undefined
                    }
                    isAsync
                  />
                  {values.repaymentsFromAllContracts !== undefined && (
                    <FormInput.Number
                      name='remainingNetAmount'
                      label={t('input.label.remainingNetAmount')}
                      suffix={` ${defaultCurrency}`}
                    />
                  )}
                  {values.repaymentsFromAllContracts !== undefined && (
                    <FormInput.Select
                      options={isRepaymentsFromAllContracts}
                      label={t('input.label.repaymentsFromAllContracts')}
                      name='repaymentsFromAllContracts'
                      onChange={option => {
                        
                        setFieldValue(
                          'repaymentsFromAllContracts',
                          option.value ? true : false
                        );

                        setFieldValue('contracts', []);
                      }}
                    />
                  )}
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                disabled={disableTabs}
                tabs={[
                  {
                    value: CASH_FLOW_TABS.file,
                    name: 'file',
                  },
                  ...(!disableTabs?.includes(CASH_FLOW_TABS.repayments)
                    ? [
                        {
                          value: CASH_FLOW_TABS.repayments,
                          name: 'repayments',
                        },
                      ]
                    : []),
                  ...(values.repaymentsFromAllContracts === false
                    ? [
                        {
                          value: CASH_FLOW_TABS.contracts,
                          name: 'contracts',
                        },
                      ]
                    : []),
                ]}
                i18nextPrefix={'cash-flow'}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

CashFlowForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'file', element: <CashFlowAddFileForm /> },
    { path: 'repayments', element: <CashFlowRepayments /> },
    { path: 'contracts', element: <CashFlowContracts /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default CashFlowForm;
